import Button from '@doordash/component-button'
import NavBar from '@doordash/component-navbar'
import {
  Text,
  Icon,
  InlineChildren,
  Stack,
  StackChildren,
} from '@doordash/design-language'
import { get } from 'lodash'
import * as React from 'react'
import { connect } from 'react-redux'
import {
  ANDROID_CLOSE_DEEPLINK,
  DASHER_ACTIVATION_HELP,
  IOS_CLOSE_DEEPLINK,
  RED_CARD,
} from '../../../constants'
import * as events from '../../../events'
import { ShippingStatus } from '../../../models/types'
import routes from '../../../routes'
import { detectWebviewOs } from '../../../utilities/userAgent'
import { loadUrl } from '../../../webview'
import { OsTypes } from '../../../webview/types'
import LinkButton from '../../atoms/link-button'
import ActivationKitModal from '../../molecules/activation-kit-modal'
import { Bottom, PackagingImage, Wrapper } from './styles'

import s from './strings'

type Props = {
  experiments?: any
  trackWebviewClosed?: () => Promise<any>
  trackActivationSplashViewed?: () => Promise<any>
}

export const ActivationKitSplash: React.FC<Props> = ({
  experiments,
  trackWebviewClosed,
  trackActivationSplashViewed,
}) => {
  const os = detectWebviewOs()
  const [isShowingModal, setShowingModal] = React.useState(false)

  const isExpired = get(experiments, 'is_instant_dashing_v2_expired', false)
  const mailingAddress = get(experiments, 'mailing_address')
  const address1 = get(mailingAddress, 'street_address', null)

  const address2 =
    get(mailingAddress, 'city') &&
    get(mailingAddress, 'state') &&
    get(mailingAddress, 'zipcode')
      ? `${mailingAddress.city}, ${mailingAddress.state} ${mailingAddress.zipcode}`
      : null

  const shippingStatus = get(experiments, 'shipping_details.shipping_status')
  const shippingArrivalDate = get(
    experiments,
    'shipping_details.shipping_arrival_date'
  )

  React.useEffect(() => {
    trackActivationSplashViewed()
  }, [])

  const toggleModal = e => {
    e.stopPropagation()
    setShowingModal(!isShowingModal)
  }

  const dasherActivationHelp = () => {
    loadUrl(DASHER_ACTIVATION_HELP)
  }

  const getTitle = () => {
    switch (shippingStatus) {
      case ShippingStatus.Shipped:
        return s.title.shipped()
      case ShippingStatus.Delivered:
        return s.title.delivered()
      case ShippingStatus.Created:
      default:
        return s.title.prepared()
    }
  }

  const getShippingDate = () => {
    switch (shippingStatus) {
      case ShippingStatus.Shipped:
        return s.shippingDate.shipped(shippingArrivalDate)
      case ShippingStatus.Delivered:
        return s.shippingDate.delivered(shippingArrivalDate)
      case ShippingStatus.Created:
      default:
        return s.shippingDate.prepared()
    }
  }

  const shippedOrDelivered = [ShippingStatus.Shipped, ShippingStatus.Delivered]

  return (
    <React.Fragment>
      {isShowingModal && (
        <ActivationKitModal
          showPackageImage={false}
          onClose={e => {
            toggleModal(e)
          }}
        />
      )}
      <NavBar
        navigationType={
          !isExpired
            ? NavBar.NavigationTypes.Close
            : NavBar.NavigationTypes.None
        }
        isSeparatorVisible={false}
        onNavigationClick={() => {
          trackWebviewClosed()
          window.location.assign(
            os === OsTypes.iOS ? IOS_CLOSE_DEEPLINK : ANDROID_CLOSE_DEEPLINK
          )
        }}
        actions={[
          {
            iconType: Icon.Types.HelpCircleLine,
            accessibilityLabel: 'help',
            onClick: dasherActivationHelp,
          },
        ]}
      />
      <Wrapper>
        <StackChildren size={StackChildren.Sizes.Medium}>
          <Text styles={Text.Styles.Bold24}>{getTitle()}</Text>
          <PackagingImage />
        </StackChildren>
        <StackChildren size={StackChildren.Sizes.Medium}>
          <Text
            color={Text.Colors.SystemRed60}
            weight={Text.Weights.Bold}
            size={Text.Sizes.S14}
            onClick={toggleModal}
          >
            {s.whatsInIt()}
          </Text>
          <InlineChildren size={InlineChildren.Sizes.XLarge}>
            <Text color={Text.Colors.SystemGrey30} weight={Text.Weights.Bold}>
              {s.statusLabel()}
            </Text>
            <Stack size={Stack.Sizes.None} style={{ 'text-align': 'start' }}>
              <Text styles={Text.Styles.Medium18}>{getShippingDate()}</Text>
              {get(experiments, 'shipping_details.shipping_url') && (
                <Text
                  color={Text.Colors.SystemRed60}
                  weight={Text.Weights.Bold}
                  size={Text.Sizes.S14}
                  onClick={() =>
                    loadUrl(experiments.shipping_details.shipping_url)
                  }
                >
                  {s.trackPackage()}
                </Text>
              )}
            </Stack>
          </InlineChildren>
          <InlineChildren size={InlineChildren.Sizes.XLarge}>
            <Text
              color={Text.Colors.SystemGrey30}
              weight={Text.Weights.Bold}
              style={{ 'white-space': 'nowrap', 'margin-right': '-4px' }}
            >
              {s.shipToLabel()}
            </Text>
            <Stack size={Stack.Sizes.None} style={{ 'text-align': 'start' }}>
              <Text styles={Text.Styles.Medium18}>{address1}</Text>
              <Text styles={Text.Styles.Medium18}>{address2}</Text>
            </Stack>
          </InlineChildren>
        </StackChildren>
        <Bottom>
          {(!shippingStatus || shippingStatus === ShippingStatus.Created) && (
            <Button
              type={Button.Types.PrimaryPill}
              size={Button.Sizes.Large}
              tag="a"
              href={
                os === OsTypes.iOS ? IOS_CLOSE_DEEPLINK : ANDROID_CLOSE_DEEPLINK
              }
              onClick={trackWebviewClosed}
            >
              {s.button.continue()}
            </Button>
          )}
          {(shippedOrDelivered.includes(shippingStatus) || isExpired ) && (
            <StackChildren>
              <LinkButton
                type={Button.Types.PrimaryPill}
                size={Button.Sizes.Large}
                href={routes.webview.buildPath({ sub: RED_CARD })}
              >
                {s.button.receivedIt()}
              </LinkButton>
              {shippingStatus === ShippingStatus.Delivered && (
                <Button
                  type={Button.Types.FlatSecondary}
                  size={Button.Sizes.Large}
                  onClick={dasherActivationHelp}
                >
                  {s.button.noReceivedIt()}
                </Button>
              )}
            </StackChildren>
          )}
        </Bottom>
      </Wrapper>
    </React.Fragment>
  )
}

ActivationKitSplash.defaultProps = {
  // tslint:disable:no-console
  trackWebviewClosed: () =>
    Promise.resolve(console.log('track webview closed')),
  trackActivationSplashViewed: () =>
    Promise.resolve(console.log('track activation splash viewed')),
  // tslint:enable:no-console
}

const mapStateToProps = state => ({
  experiments: state.experiments,
})

const mapDispatchToProps = {
  trackWebviewClosed: events.trackWebviewClosed,
  trackActivationSplashViewed: events.trackActivationSplashViewed,
}

export default connect(mapStateToProps, mapDispatchToProps)(ActivationKitSplash)
